import { XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import mediaUpload from "@src/api/media/upload";
import videoUpload from "@src/api/video/upload/direct";
import Button from "@src/components/elements/input/Button";

type Props = {
  onChange: (media: {
    id: string;
    key: string;
    file_name: string;
    type: string;
    thumbnail: string;
    data_url?: string;
  }) => void;
  onDelete: (id: string) => void;
  media: Array<{
    id: string;
    key: string;
    file_name: string;
    type: string;
    thumbnail: string;
    data_url?: string;
  }>;
  addClick?: {
    getFunction: (() => void) | undefined;
    setFunction: Dispatch<SetStateAction<(() => void) | undefined>>;
  };
  tooltip?: string;
};
const JobMedia = ({ onChange, media, onDelete, addClick, tooltip }: Props) => {
  const { t } = useTranslation();
  const [uploadingCount, setUploadingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadMutation = useMutation({
    mutationKey: ["upload"],
    mutationFn: async (file: File) => {
      let key = "";
      let id = "";
      let thumbnail = "";
      let data_url = "";
      if (file.type.startsWith("video")) {
        const { data } = await videoUpload({ params: { videoNames: file.name } });
        key = data.key;
        id = data.id;
        thumbnail = data.thumbnail;
      }

      const onUploadProgress = (event: AxiosProgressEvent) => {
        const percentage = Math.round((event.loaded * 100) / (event.total ?? 1));
        setProgress(percentage);
      };

      let res;
      if (file.type.startsWith("video")) {
        res = await mediaUpload({ file }, onUploadProgress);
      } else {
        setLoading(true);
        res = await mediaUpload({ file }, onUploadProgress);
        setLoading(false);
      }
      if (!file.type.startsWith("video")) {
        key = res.data.key;
        id = res.data.id;
        data_url = res.data.data_url;
      }
      setUploadingCount(count => count - 1);
      return {
        key,
        id,
        file_name: file.name,
        type: file.type.startsWith("video") ? "video" : "image",
        thumbnail,
        data_url,
      };
    },
    onSuccess: data => {
      onChange(data);
    },
    onError: () => {
      alert("Failed");
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const addMedia = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  return (
    <>
      <div className="text-sm font-semibold">
        {t("main.profileViewTabs.aboutMe.content.sidebarButtons.media")}
      </div>

      {tooltip && <div className="text-sm">{tooltip}</div>}
      <div className="grid grid-cols-4 gap-3 rounded-xl lg:border lg:p-3">
        {media.map(data => (
          <div key={data.id} className="relative">
            <button
              onClick={() => {
                onDelete(data.id);
              }}
              className="absolute right-3 top-3"
            >
              <XMarkIcon className="size-6 rounded-full bg-white opacity-90" />
            </button>
            {data.type === "image" && (
              <img className="rounded-xl bg-gray-200" src={data.data_url} />
            )}
            {data.type === "video" && (
              <img className="rounded-xl bg-gray-200" src={data.thumbnail} />
            )}
          </div>
        ))}
        {[...Array(uploadingCount)].map(count => (
          <div
            key={count}
            className="relative block max-w-sm items-center rounded-lg border border-gray-100 bg-white p-6 shadow-md dark:border-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            {loading && (
              <div className="absolute left-1/2 top-2/4 -translate-x-1/2 -translate-y-1/2">
                <svg
                  aria-hidden="true"
                  className="size-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                {progress}%<span className="sr-only"></span>
              </div>
            )}
          </div>
        ))}
      </div>
      {!addClick && (
        <div className="ml-auto w-fit">
          <Button
            size={Button.Sizes.Small}
            variant={Button.Variants.Transparent}
            onClick={addMedia}
          >
            {t("main.feedTabs.buttons.addMedia")}
          </Button>
        </div>
      )}

      <div className="mb-3">
        <input
          type="file"
          className="border-secondary-500 text-surface file:text-surface focus:border-primary focus:shadow-inset relative m-0 ml-auto block w-full min-w-0 flex-auto cursor-pointer rounded border border-solid bg-transparent bg-clip-padding px-3 py-[0.32rem] text-base font-normal transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:me-3 file:cursor-pointer file:overflow-hidden file:rounded-none file:border-0 file:border-e file:border-solid file:border-inherit file:bg-transparent file:px-3 file:py-[0.32rem] focus:text-gray-700 focus:outline-none dark:border-white/70 dark:text-white file:dark:text-white"
          accept="image/*,video/*"
          onChange={e => {
            if (e.target.files && e.target.files.length + uploadingCount + media.length > 10) {
              alert("Max 10 Bilder");
              e.target.value = "";
              return;
            }
            for (const file of e.target.files || []) {
              setUploadingCount(count => count + 1);
              uploadMutation.mutate(file);
            }
            e.target.value = "";
          }}
          multiple={true}
          ref={fileInputRef}
        />
      </div>
    </>
  );
};

export default JobMedia;
